import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import { BankTransactions } from "../../../api/BankService";
import { SweetAlertShow, LoadingDiv } from "../../master/Master";
import BankLayout from "../../Accounts/BankLayout";
import { imgBaseUrl } from "../../../api/api";

/* ---------------- helpers ---------------- */

const money = (v) =>
  `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(String(v).replace(" ", "T"));
  return isNaN(d)
    ? v
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const toISO = (d) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

const getRange = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case "today":
      return [toISO(now), toISO(now)];
    case "month":
      return [toISO(new Date(y, m, 1)), toISO(now)];
    case "lastMonth":
      return [toISO(new Date(y, m - 1, 1)), toISO(new Date(y, m, 0))];
    case "fy": {
      const start = m >= 3 ? y : y - 1; // Indian FY: Apr - Mar
      return [`${start}-04-01`, toISO(now)];
    }
    default:
      return ["", ""];
  }
};

const QUICK = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "month", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "fy", label: "This FY" },
];

const imgUrl = (p) => (p ? `${imgBaseUrl}${p}` : null);

/* ---------------- component ---------------- */

export default function BankTransactionList() {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [tab, setTab] = useState("payments");
  const [quick, setQuick] = useState("all");
  const [filters, setFilters] = useState({ from_date: "", to_date: "" });
  const [applied, setApplied] = useState({ from_date: "", to_date: "" });
  const [pages, setPages] = useState({ payment_page: 1, expense_page: 1 });
  const [data, setData] = useState(null);

  const buildParams = useCallback(
    (extra = {}) => {
      const p = { bank_id: id, per_page: 20, ...extra };
      if (applied.from_date) p.from_date = applied.from_date;
      if (applied.to_date) p.to_date = applied.to_date;
      return p;
    },
    [id, applied],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await BankTransactions(buildParams(pages));
      if (res?.data?.status) setData(res.data.data);
      else SweetAlertShow("info", "Warning", res?.data?.message);
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [buildParams, pages]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------------- filters ---------------- */

  const applyQuick = (key) => {
    const [from, to] = getRange(key);
    const next = { from_date: from, to_date: to };
    setQuick(key);
    setFilters(next);
    setApplied(next);
    setPages({ payment_page: 1, expense_page: 1 });
  };

  const applyCustom = () => {
    if (
      filters.from_date &&
      filters.to_date &&
      filters.to_date < filters.from_date
    ) {
      SweetAlertShow(
        "warning",
        "Validation",
        "To date cannot be before From date.",
      );
      return;
    }
    setQuick("custom");
    setPages({ payment_page: 1, expense_page: 1 });
    setApplied(filters);
  };

  /* ---------------- print (no pagination) ---------------- */

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const res = await BankTransactions(buildParams({ print: 1 }));
      if (!res?.data?.status) {
        SweetAlertShow("info", "Warning", res?.data?.message);
        return;
      }

      const { bank, summary, payments, expenses } = res.data.data;
      const range =
        applied.from_date || applied.to_date
          ? `${applied.from_date ? fmtDate(applied.from_date) : "Start"} to ${
              applied.to_date ? fmtDate(applied.to_date) : "Today"
            }`
          : "All dates";

      const paymentRows = payments.data
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td>${fmtDate(r.payment_date)}</td>
            <td>${esc(r.receipt_no)}</td>
            <td>${esc(r.module_label)}</td>
            <td>${esc(r.payment_method)}</td>
            <td>${esc(r.transaction_no)}</td>
            <td>${esc(r.remark)}</td>
            <td class="r">${money(r.amount)}</td></tr>`,
        )
        .join("");

      const expenseRows = expenses.data
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td>${fmtDate(r.date || r.created_at)}</td>
            <td>${esc(r.type)}</td>
            <td>${esc(r.description)}</td>
            <td>${esc(r.payment_type)}</td>
            <td class="r">${money(r.amount)}</td></tr>`,
        )
        .join("");

      const html = `<!doctype html><html><head><title>${esc(bank?.account_no)} - Transactions</title>
        <style>
          body{font-family:Arial,sans-serif;font-size:12px;padding:20px;color:#222}
          h2{margin:0 0 4px} h3{margin:22px 0 8px}
          .meta{color:#555;margin-bottom:12px}
          .sum{display:flex;gap:30px;margin:12px 0;padding:10px;border:1px solid #ccc}
          table{width:100%;border-collapse:collapse}
          th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}
          th{background:#f2f2f2} .r{text-align:right}
          tr{page-break-inside:avoid} thead{display:table-header-group}
        </style></head><body>
        <h2>${esc(bank?.account_no)} - Transactions</h2>
        <div class="meta">${esc(bank?.owner_name || "")} | Period: ${range}</div>
        <div class="sum">
          <div>Total Payments: <b>${money(summary.total_payment)}</b></div>
          <div>Total Expenses: <b>${money(summary.total_expense)}</b></div>
          <div>Net: <b>${money(summary.balance_flow)}</b></div>
        </div>
        <h3>Payments (${payments.data.length})</h3>
        <table><thead><tr><th>#</th><th>Date</th><th>Receipt</th><th>Module</th><th>Method</th><th>Txn No</th><th>Remark</th><th class="r">Amount</th></tr></thead>
        <tbody>${paymentRows || '<tr><td colspan="8">No payments</td></tr>'}</tbody></table>
        <h3>Expenses (${expenses.data.length})</h3>
        <table><thead><tr><th>#</th><th>Date</th><th>Type</th><th>Description</th><th>Payment Type</th><th class="r">Amount</th></tr></thead>
        <tbody>${expenseRows || '<tr><td colspan="6">No expenses</td></tr>'}</tbody></table>
        </body></html>`;

      const w = window.open("", "_blank");
      if (!w) {
        SweetAlertShow("warning", "Popup blocked", "Allow popups to print.");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setPrinting(false);
    }
  };

  /* ---------------- derived ---------------- */

  const bank = data?.bank;
  const summary = data?.summary;
  const list = tab === "payments" ? data?.payments : data?.expenses;
  const rows = list?.data || [];
  const pageKey = tab === "payments" ? "payment_page" : "expense_page";
  const pageTotal = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  const summaryCards = [
    {
      label: "Current Balance",
      value: bank?.amount,
      icon: "ri-wallet-3-line",
      tone: "primary",
      sign: "",
    },
    {
      label: "Received",
      value: summary?.total_payment,
      icon: "ri-arrow-down-circle-line",
      tone: "success",
      sign: "+ ",
    },
    {
      label: "Expenses",
      value: summary?.total_expense,
      icon: "ri-arrow-up-circle-line",
      tone: "danger",
      sign: "− ",
    },
    {
      label: "Net (Received − Expense)",
      value: summary?.balance_flow,
      icon: "ri-scales-3-line",
      tone: "info",
      sign: "",
    },
  ];

  const renderPager = () =>
    list && list.total > 0 ? (
      <div className="bt-footer">
        <div className="bt-footer-info">
          Showing <b>{list.from}</b>–<b>{list.to}</b> of <b>{list.total}</b>
          <span className="bt-page-total">
            Page total: <b>{money(pageTotal)}</b>
          </span>
        </div>

        {list.last_page > 1 && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-label-secondary"
              disabled={list.current_page <= 1}
              onClick={() =>
                setPages((p) => ({ ...p, [pageKey]: p[pageKey] - 1 }))
              }
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="small text-muted">
              {list.current_page} / {list.last_page}
            </span>
            <button
              className="btn btn-sm btn-label-secondary"
              disabled={list.current_page >= list.last_page}
              onClick={() =>
                setPages((p) => ({ ...p, [pageKey]: p[pageKey] + 1 }))
              }
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    ) : null;

  const proofLink = (path) =>
    path ? (
      <a
        href={imgUrl(path)}
        target="_blank"
        rel="noreferrer"
        className="bt-proof"
        title="View proof"
      >
        <i className="ri-image-line"></i>
      </a>
    ) : (
      <span className="text-muted">-</span>
    );

  /* ---------------- render ---------------- */

  return (
    <>
      {(loading || printing) && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />

            <div className="container-fluid bt-container">
              <BankLayout />

              {/* BANK HEADER */}
              <div className="bt-hero">
                <div className="d-flex align-items-center gap-3">
                  <Link
                    to="/settings/bank"
                    className="btn btn-icon btn-label-secondary"
                    title="Back to accounts"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </Link>

                  <div className="bt-hero-icon">
                    <i className="ri-bank-line"></i>
                  </div>

                  <div>
                    <h4 className="fw-bold mb-1">
                      {bank?.account_no || "Account"}
                    </h4>
                    <div className="bt-hero-meta">
                      {bank?.owner_name && (
                        <span>
                          <i className="ri-user-3-line"></i>
                          {bank.owner_name}
                        </span>
                      )}
                      {bank?.ifsc_code && (
                        <span>
                          <i className="ri-hashtag"></i>
                          {bank.ifsc_code}
                        </span>
                      )}
                      {bank?.branch && (
                        <span>
                          <i className="ri-map-pin-line"></i>
                          {bank.branch}
                        </span>
                      )}
                      {bank?.upi_id && (
                        <span>
                          <i className="ri-at-line"></i>
                          {bank.upi_id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handlePrint}
                  disabled={printing}
                >
                  <i className="ri-printer-line me-1"></i>
                  Print
                </button>
              </div>

              {/* SUMMARY */}
              <div className="row g-3 mb-4">
                {summaryCards.map((c) => (
                  <div className="col-xl-3 col-md-6" key={c.label}>
                    <div className="bt-stat">
                      <div className={`bt-stat-icon bg-label-${c.tone}`}>
                        <i className={c.icon}></i>
                      </div>
                      <div>
                        <small className="text-muted">{c.label}</small>
                        <h4 className={`fw-bold mb-0 text-${c.tone}`}>
                          {c.sign}
                          {money(c.value)}
                        </h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* MAIN CARD */}
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                {/* TOOLBAR */}
                <div className="bt-toolbar">
                  <div className="bt-segment">
                    <button
                      className={tab === "payments" ? "active" : ""}
                      onClick={() => setTab("payments")}
                    >
                      <i className="ri-arrow-down-circle-line"></i>
                      Payments
                      <span className="bt-count success">
                        {data?.payments?.total ?? 0}
                      </span>
                    </button>
                    <button
                      className={tab === "expenses" ? "active" : ""}
                      onClick={() => setTab("expenses")}
                    >
                      <i className="ri-arrow-up-circle-line"></i>
                      Expenses
                      <span className="bt-count danger">
                        {data?.expenses?.total ?? 0}
                      </span>
                    </button>
                  </div>

                  <div className="bt-filters">
                    <div className="bt-chips">
                      {QUICK.map((q) => (
                        <button
                          key={q.key}
                          className={quick === q.key ? "active" : ""}
                          onClick={() => applyQuick(q.key)}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>

                    <div className="bt-range">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.from_date}
                        onChange={(e) =>
                          setFilters({ ...filters, from_date: e.target.value })
                        }
                      />
                      <span className="text-muted small">to</span>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.to_date}
                        min={filters.from_date || undefined}
                        onChange={(e) =>
                          setFilters({ ...filters, to_date: e.target.value })
                        }
                      />
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={applyCustom}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>

                {/* TABLE */}
                <div className="table-responsive">
                  {tab === "payments" ? (
                    <table className="table align-middle mb-0 bt-table">
                      <thead>
                        <tr>
                          <th className="ps-4">Date</th>
                          <th>Receipt</th>
                          <th>For</th>
                          <th>Method</th>
                          <th>Txn No</th>
                          <th>Remark</th>
                          <th className="text-center">Proof</th>
                          <th className="text-end pe-4">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length ? (
                          rows.map((r) => (
                            <tr key={r.id}>
                              <td className="ps-4 text-nowrap">
                                {fmtDate(r.payment_date)}
                              </td>
                              <td>
                                <span className="bt-mono">
                                  {r.receipt_no || "-"}
                                </span>
                              </td>
                              <td>
                                <div className="bt-badges">
                                  {r.modules?.length ? (
                                    r.modules.map((m, i) => (
                                      <span
                                        key={i}
                                        className="bt-badge primary"
                                        title={money(m.amount)}
                                      >
                                        {m.type || "Fee"}
                                        {r.modules.length > 1 && (
                                          <em>{money(m.amount)}</em>
                                        )}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`bt-badge ${
                                    r.payment_method === "Cash"
                                      ? "success"
                                      : "info"
                                  }`}
                                >
                                  {r.payment_method}
                                </span>
                              </td>
                              <td>
                                <span className="bt-mono">
                                  {r.transaction_no || "-"}
                                </span>
                              </td>
                              <td className="bt-remark">{r.remark || "-"}</td>
                              <td className="text-center">
                                {proofLink(r.image)}
                              </td>
                              <td className="text-end pe-4">
                                <span className="bt-amount in">
                                  + {money(r.amount)}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8">
                              <div className="bt-empty">
                                <i className="ri-inbox-line"></i>
                                <h6>No payments found</h6>
                                <p>Try changing the date range.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="table align-middle mb-0 bt-table">
                      <thead>
                        <tr>
                          <th className="ps-4">Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Payment Type</th>
                          <th className="text-center">Proof</th>
                          <th className="text-end pe-4">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length ? (
                          rows.map((r) => (
                            <tr key={r.id}>
                              <td className="ps-4 text-nowrap">
                                {fmtDate(r.date || r.created_at)}
                              </td>
                              <td>
                                <span className="bt-badge warning">
                                  {r.type || "-"}
                                </span>
                              </td>
                              <td className="bt-remark">
                                {r.description || "-"}
                              </td>
                              <td>{r.payment_type || "-"}</td>
                              <td className="text-center">
                                {proofLink(r.image)}
                              </td>
                              <td className="text-end pe-4">
                                <span className="bt-amount out">
                                  − {money(r.amount)}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6">
                              <div className="bt-empty">
                                <i className="ri-inbox-line"></i>
                                <h6>No expenses found</h6>
                                <p>Try changing the date range.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {renderPager()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bt-container { padding-top: 18px; padding-bottom: 30px; }

        /* hero */
        .bt-hero {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 16px; margin-bottom: 20px;
        }
        .bt-hero-icon {
          width: 52px; height: 52px; min-width: 52px; border-radius: 15px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(var(--bs-primary-rgb), .11);
          color: var(--bs-primary); font-size: 25px;
        }
        .bt-hero-meta {
          display: flex; flex-wrap: wrap; gap: 6px 14px;
          font-size: 12px; color: var(--bs-secondary-color);
        }
        .bt-hero-meta i { margin-right: 4px; }

        /* stat cards */
        .bt-stat {
          height: 100%; display: flex; align-items: center; gap: 14px;
          padding: 16px; border-radius: 15px;
          border: 1px solid var(--bs-border-color);
          background: var(--bs-card-bg, var(--bs-body-bg));
        }
        .bt-stat-icon {
          width: 44px; height: 44px; min-width: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; font-size: 21px;
        }
        .bt-stat h4 { font-size: 18px; }

        /* toolbar */
        .bt-toolbar {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 14px; padding: 16px 20px;
          border-bottom: 1px solid var(--bs-border-color);
        }
        .bt-segment {
          display: inline-flex; padding: 4px; gap: 4px; border-radius: 11px;
          background: rgba(var(--bs-secondary-rgb), .08);
        }
        .bt-segment button {
          display: flex; align-items: center; gap: 6px; border: 0;
          padding: 7px 14px; border-radius: 8px; background: transparent;
          font-size: 13px; font-weight: 600; color: var(--bs-secondary-color);
        }
        .bt-segment button.active {
          background: var(--bs-card-bg, #fff); color: var(--bs-body-color);
          box-shadow: 0 2px 8px rgba(0,0,0,.08);
        }
        .bt-count {
          padding: 1px 8px; border-radius: 20px; font-size: 11px; font-weight: 700;
        }
        .bt-count.success { background: rgba(var(--bs-success-rgb), .14); color: var(--bs-success); }
        .bt-count.danger  { background: rgba(var(--bs-danger-rgb), .14);  color: var(--bs-danger); }

        .bt-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
        .bt-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .bt-chips button {
          border: 1px solid var(--bs-border-color); background: transparent;
          padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
          color: var(--bs-secondary-color);
        }
        .bt-chips button.active {
          background: var(--bs-primary); border-color: var(--bs-primary); color: #fff;
        }
        .bt-range { display: flex; align-items: center; gap: 6px; }
        .bt-range .form-control { width: 140px; }

        /* table */
        .bt-table thead th {
          padding: 12px; font-size: 10px; text-transform: uppercase;
          letter-spacing: .5px; white-space: nowrap;
          background: rgba(var(--bs-secondary-rgb), .035);
          color: var(--bs-secondary-color);
        }
        .bt-table tbody td {
          padding: 13px 12px; font-size: 13px;
          border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .bt-table tbody tr:last-child td { border-bottom: 0; }
        .bt-table tbody tr:hover { background: rgba(var(--bs-primary-rgb), .025); }

        .bt-mono { font-family: monospace; font-size: 12px; }
        .bt-remark { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .bt-badges { display: flex; flex-wrap: wrap; gap: 4px; }

        .bt-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 3px 9px; border-radius: 6px;
          font-size: 11px; font-weight: 700; white-space: nowrap;
        }
        .bt-badge em { font-style: normal; font-weight: 500; opacity: .75; }
        .bt-badge.primary { background: rgba(var(--bs-primary-rgb), .10); color: var(--bs-primary); }
        .bt-badge.success { background: rgba(var(--bs-success-rgb), .12); color: var(--bs-success); }
        .bt-badge.info    { background: rgba(var(--bs-info-rgb), .12);    color: var(--bs-info); }
        .bt-badge.warning { background: rgba(var(--bs-warning-rgb), .14); color: var(--bs-warning-text-emphasis, #b07800); }

        .bt-amount { font-weight: 700; white-space: nowrap; }
        .bt-amount.in  { color: var(--bs-success); }
        .bt-amount.out { color: var(--bs-danger); }

        .bt-proof {
          display: inline-flex; width: 30px; height: 30px; border-radius: 8px;
          align-items: center; justify-content: center; font-size: 16px;
          background: rgba(var(--bs-info-rgb), .10); color: var(--bs-info);
        }

        /* footer */
        .bt-footer {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 10px; padding: 12px 20px;
          border-top: 1px solid var(--bs-border-color); font-size: 12px;
          background: rgba(var(--bs-secondary-rgb), .025);
        }
        .bt-page-total { margin-left: 16px; color: var(--bs-secondary-color); }

        /* empty */
        .bt-empty { text-align: center; padding: 50px 20px; color: var(--bs-secondary-color); }
        .bt-empty i { font-size: 38px; opacity: .5; }
        .bt-empty h6 { margin: 8px 0 2px; }
        .bt-empty p { margin: 0; font-size: 12px; }

        @media (max-width: 767.98px) {
          .bt-toolbar { padding: 14px; }
          .bt-range { width: 100%; flex-wrap: wrap; }
          .bt-range .form-control { flex: 1; min-width: 120px; }
          .bt-hero .btn-primary { width: 100%; }
        }
      `}</style>
    </>
  );
}
